/* eslint-disable promise/catch-or-return */

import UIButton from 'UIComponents/button/UIButton';
import UISelectableBox from 'UIComponents/button/UISelectableBox';
import { useEffect, useMemo, useRef, useState } from 'react';
import UpgradeProductProperties, { UpgradeProductPropertiesFromApiName } from 'self-service-api/constants/UpgradeProductProperties';
import { getUpgradeButtonText, getUpgradeButtonTextFromApiName } from 'ui-addon-upgrades/_core/common/adapters/getUpgradeButtonText';
import { track } from 'ui-addon-upgrades/_core/common/eventTracking/tracker';
import { omit } from 'ui-addon-upgrades/utils';
import { PricingPageRedirectButtonPropsInterface } from './interfaces/pricingPageRedirectButtonInterface';
// @ts-expect-error dependency missing types
import UpgradeProductToApiNameMap from 'self-service-api/constants/UpgradeProductToApiNameMap';
import { getUpgradePageUrl, productsAndAddons, suiteStarter, trialPricingPage } from 'self-service-api/core/utilities/links';
import { getHubAndTierFromApiName, getSource, getUpgradeDataTrackingProperties, mapPurchaseMotionApiName, trackPotentialBannerInteraction } from 'ui-addon-upgrades/ums/utils';
import Raven from 'raven-js';
import { getCanViewNewSeats } from '../_core/common/eligibility/getCanViewNewSeats';
import { PRICING_PAGE_REDIRECT } from '../_core/purchaseMotions/PurchaseMotionTypes';
import { trackFailedApiNameMap, trackPurchaseMotionFailure } from '../_core/utils/metricUtils';
import { jsx as _jsx } from "react/jsx-runtime";
const UIPricingPageRedirectButton = props => {
  const _isMounted = useRef(true);
  const {
    upgradeData,
    usageLimitBannerData,
    isSelectableBox,
    subscreen,
    children,
    isUpgradePoint,
    onClick,
    level,
    goToUpgradePage,
    canViewNewSeats,
    goToTrialPricingPage
  } = props;
  const upgradeDataWithApiName = useMemo(() => {
    return Object.assign({}, upgradeData, {
      apiName: mapPurchaseMotionApiName(canViewNewSeats, upgradeData.upgradeProduct, 'UIPricingPageRedirectButton', upgradeData.apiName)
    });
  }, [canViewNewSeats, upgradeData]);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const [fetchedCanViewNewSeats, setFetchedCanViewNewSeats] = useState(canViewNewSeats);
  useEffect(() => {
    if (typeof canViewNewSeats === 'undefined' && typeof fetchedCanViewNewSeats === 'undefined') {
      getCanViewNewSeats().then(res => {
        setFetchedCanViewNewSeats(res);
      }).catch(() => {
        setFetchedCanViewNewSeats(false);
      });
    }
  });
  const propsToPass = omit(props, 'usageLimitBannerData', 'isSelectableBox', 'upgradeData', 'subscreen', 'isUpgradePoint', 'onClick', 'level', 'canViewNewSeats', 'goToUpgradePage', 'goToTrialPricingPage');
  const source = getSource(upgradeDataWithApiName.app, upgradeDataWithApiName.screen, upgradeDataWithApiName.uniqueId);
  const getUpgradeLink = () => {
    if (canViewNewSeats && upgradeDataWithApiName.apiName) {
      const mappedProperties = UpgradeProductPropertiesFromApiName[upgradeDataWithApiName.apiName];
      return mappedProperties && mappedProperties.upgradeLink ? mappedProperties.upgradeLink : undefined;
    }
    return (UpgradeProductProperties[upgradeDataWithApiName.upgradeProduct] || {}).upgradeLink;
  };
  const upgradeLink = getUpgradeLink();
  useEffect(() => {
    if ((typeof fetchedCanViewNewSeats !== 'undefined' || typeof canViewNewSeats !== 'undefined') && !hasTrackedView) {
      upgradeDataWithApiName.apiName = mapPurchaseMotionApiName(fetchedCanViewNewSeats || canViewNewSeats, upgradeDataWithApiName.upgradeProduct, 'UIPricingPageRedirectButton', upgradeDataWithApiName.apiName);
      track('pricingPageRedirectButtonInteraction', {
        action: 'viewed',
        upgradeProduct: upgradeDataWithApiName.upgradeProduct,
        source,
        subscreen,
        apiName: upgradeDataWithApiName.apiName
      });
      if (isUpgradePoint) {
        track('interaction', Object.assign({
          action: 'viewed pricing page redirect',
          isPrimaryCta: level === 1,
          isSecondaryCta: level === 2
        }, usageLimitBannerData, getUpgradeDataTrackingProperties(upgradeDataWithApiName)));
      }
      setHasTrackedView(true);
    }
  }, [upgradeDataWithApiName, source, subscreen, isUpgradePoint, level, usageLimitBannerData, fetchedCanViewNewSeats, canViewNewSeats, hasTrackedView]);
  useEffect(() => {
    return function cleanup() {
      _isMounted.current = false;
    };
  }, []);
  const apiNameToUse = fetchedCanViewNewSeats && upgradeDataWithApiName.apiName || UpgradeProductToApiNameMap[upgradeDataWithApiName.upgradeProduct];
  const getUpgradeText = () => {
    if (fetchedCanViewNewSeats && upgradeDataWithApiName.apiName) {
      return getUpgradeButtonTextFromApiName('upgrade', upgradeDataWithApiName.apiName);
    }
    return getUpgradeButtonText('upgrade', upgradeDataWithApiName.upgradeProduct);
  };
  const getRedirectLink = () => {
    if (goToTrialPricingPage) {
      return trialPricingPage(upgradeDataWithApiName.uniqueId, apiNameToUse);
    }

    // For Upgrade page
    // this is also used in the UMS banners
    if (goToUpgradePage) {
      if (usageLimitBannerData) {
        return getUpgradePageUrl(upgradeDataWithApiName.uniqueId, {
          limit: usageLimitBannerData.limit,
          value: usageLimitBannerData.value
        });
      }
      return suiteStarter(upgradeDataWithApiName.uniqueId);
    }
    if (!apiNameToUse) {
      trackFailedApiNameMap('getRedirectLink', upgradeDataWithApiName.apiName || 'no api name', upgradeDataWithApiName.upgradeProduct);
      Raven.captureException(new Error(`failed to get redirect link from apiName ${upgradeDataWithApiName.apiName} in getRedirectLink`));
      trackPurchaseMotionFailure({
        uniqueId: upgradeData.uniqueId,
        apiName: upgradeData.apiName || UpgradeProductToApiNameMap[upgradeData.upgradeProduct],
        purchaseMotion: PRICING_PAGE_REDIRECT
      });
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return upgradeLink(upgradeDataWithApiName.uniqueId);
    }
    if (upgradeLink) {
      const {
        tier
      } = getHubAndTierFromApiName(apiNameToUse);
      return upgradeLink(upgradeDataWithApiName.uniqueId, tier);
    }
    return productsAndAddons(); //fallback generic pricing page link
  };
  const handleUpgrade = () => {
    track('pricingPageRedirectButtonInteraction', {
      action: 'clicked',
      upgradeProduct: upgradeDataWithApiName.upgradeProduct,
      source,
      subscreen,
      apiName: upgradeDataWithApiName.apiName
    });
    if (isUpgradePoint) {
      track('interaction', Object.assign({
        action: 'clicked upgrade point'
      }, usageLimitBannerData, getUpgradeDataTrackingProperties(upgradeDataWithApiName)));
      track('interaction', Object.assign({
        action: 'clicked pricing page redirect',
        isPrimaryCta: level === 1,
        isSecondaryCta: level === 2
      }, usageLimitBannerData, getUpgradeDataTrackingProperties(upgradeDataWithApiName)));
      const {
        pointType,
        upgradeKey
      } = upgradeDataWithApiName;
      trackPotentialBannerInteraction(pointType, upgradeKey).catch(error => {
        Raven.captureException(new Error('Failed to submit interaction from UIPricingPageRedirectButton'), {
          extra: {
            message: error.message,
            uniqueId: upgradeData.uniqueId
          }
        });
      });
    }
    if (onClick) onClick();
  };
  if (!upgradeLink) return null;
  if (isSelectableBox) {
    return /*#__PURE__*/_jsx(UISelectableBox, Object.assign({
      onClick: handleUpgrade
    }, propsToPass, {
      children: children
    }));
  }
  return /*#__PURE__*/_jsx(UIButton, Object.assign({
    target: "_blank",
    "data-reagan-primary-cta": true,
    "data-selenium": "pricing-page-redirect-button",
    "data-test-id": "pricing-page-redirect-button",
    onClick: handleUpgrade,
    href: getRedirectLink(),
    external: true,
    iconProps: {
      className: 'hidden'
    }
  }, propsToPass, {
    children: children || getUpgradeText()
  }));
};
UIPricingPageRedirectButton.defaultProps = {
  use: 'primary',
  isSelectableBox: false // only use this prop when absolutely needed
};
UIPricingPageRedirectButton.propTypes = PricingPageRedirectButtonPropsInterface;
export default UIPricingPageRedirectButton;